import { z } from 'zod';

export type SortDir = 'asc' | 'desc';
export interface SortState<K extends string = string> { key: K; dir: SortDir }

/** Bấm lần 1 → tăng dần, lần 2 → giảm dần, lần 3 → bỏ sắp (về thứ tự mặc định). */
export function nextSort<K extends string>(cur: SortState<K> | null, key: K): SortState<K> | null {
  if (!cur || cur.key !== key) return { key, dir: 'asc' };
  return cur.dir === 'asc' ? { key, dir: 'desc' } : null;
}

export const formatSort = (s: SortState | null): string | undefined => (s ? `${s.key}.${s.dir}` : undefined);

/** `cột.asc|desc` → trạng thái; sai dạng hoặc cột không nằm trong `allowed` thì coi như không sắp. */
export function parseSort<K extends string>(raw: unknown, allowed: readonly K[]): SortState<K> | null {
  if (typeof raw !== 'string') return null;
  const i = raw.lastIndexOf('.');
  if (i <= 0) return null;
  const key = raw.slice(0, i) as K;
  const dir = raw.slice(i + 1);
  if ((dir !== 'asc' && dir !== 'desc') || !allowed.includes(key)) return null;
  return { key, dir };
}

export type SortValue = string | number | null | undefined;

const isEmpty = (v: SortValue) => v === null || v === undefined || v === '' || (typeof v === 'number' && Number.isNaN(v));

function compare(a: string | number, b: string | number): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  // Ngày ISO so bằng chuỗi cũng đúng thứ tự; chữ tiếng Việt so theo bảng chữ vi.
  return String(a).localeCompare(String(b), 'vi');
}

/**
 * Sắp bản sao của `rows` theo một cột. Ô trống (null, undefined, '', NaN) luôn xếp cuối dù tăng hay giảm.
 * Ổn định: bằng nhau thì giữ thứ tự gốc (cả khi giảm dần). `sort` null → trả đúng thứ tự gốc.
 */
export function sortBy<T>(rows: readonly T[], sort: SortState | null, accessor: (row: T, key: string) => SortValue): T[] {
  if (!sort) return [...rows];
  const sign = sort.dir === 'asc' ? 1 : -1;
  return rows
    .map((row, i) => ({ row, i, v: accessor(row, sort.key) }))
    .sort((x, y) => {
      const ex = isEmpty(x.v);
      const ey = isEmpty(y.v);
      if (ex || ey) return ex === ey ? x.i - y.i : ex ? 1 : -1;
      return sign * compare(x.v as string | number, y.v as string | number) || x.i - y.i;
    })
    .map((x) => x.row);
}

/** Cho validateSearch của route: dạng sai coi như không có tham số (không làm hỏng route). */
export const sortSearchParam = z.unknown().transform((v) => (typeof v === 'string' && /^\S{1,120}\.(asc|desc)$/.test(v) ? v : undefined)).optional();
